import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchGuestWishlist } from '../api/guestApi';
import WishlistToggle from '../components/guest/WishlistToggle';
import { useCurrency } from '../components/CurrencyContext';
import { getLocalItem, getSessionItem } from '../utils/safeStorage';
import { useSearchContext } from '../../context/SearchContext.jsx';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';
import { publicBrandText } from '../../utils/brandText';
import { resolveStateName } from '../../utils/locationState';
import { buildElivaasDetailPath, extractElivaasProviderId } from '../../utils/providerRoutes';
import RatingBadge from '../../components/reviews/RatingBadge';

const resolveApiBase = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    '';
  if (typeof window === 'undefined') return envBase || '';
  if (!envBase) return '';
  if (process.env.NODE_ENV !== 'production' && envBase.replace(/\/$/, '') === window.location.origin.replace(/\/$/, '')) {
    return '';
  }
  return envBase;
};

const API_BASE = resolveApiBase();
const debugLog = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[ListingPage]', ...args);
  }
};

const normalizePublicPropertiesDateParams = (path) => {
  if (!path || typeof path !== 'string' || !path.startsWith('/api/public/properties')) return path;
  try {
    const url = new URL(path, 'https://placeholder.invalid');
    const lowerCheckIn = url.searchParams.get('checkin');
    const lowerCheckOut = url.searchParams.get('checkout');
    if (lowerCheckIn && !url.searchParams.get('checkIn')) url.searchParams.set('checkIn', lowerCheckIn);
    if (lowerCheckOut && !url.searchParams.get('checkOut')) url.searchParams.set('checkOut', lowerCheckOut);
    url.searchParams.delete('checkin');
    url.searchParams.delete('checkout');
    return `${url.pathname}${url.search}`;
  } catch (_) {
    return path;
  }
};

const fetchJson = async (path, { method = 'GET', body, signal } = {}) => {
  const normalizedPath = normalizePublicPropertiesDateParams(path);
  const res = await fetch(`${API_BASE}${normalizedPath}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    signal,
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const data = JSON.parse(text);
      msg = data.message || text;
    } catch (_) {
      // keep text
    }
    const err = new Error(msg || 'Request failed');
    err.status = res.status;
    throw err;
  }
  return res.json();
};

const normalizeText = (value) => (value || '').toString().trim();
const normalizeAmenityLabel = (amenity) => {
  const value = amenity?.name || amenity?.label || amenity?.title || amenity?.slug || amenity;
  return normalizeText(value ? String(value).replace(/_/g, ' ') : '');
};
const extractAmenityValues = (source) => {
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') {
    return source
      .split(/[|,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (source && typeof source === 'object') {
    if (Array.isArray(source.items)) return source.items;
    if (Array.isArray(source.list)) return source.list;
    if (Array.isArray(source.names)) return source.names;
  }
  return [];
};
const collectAmenityTags = (item) => {
  const sources = [
    item?.tags,
    item?.amenities,
    item?.amenities?.items,
    item?.amenityTags,
    item?.amenity_tags,
    item?.amenityNames,
    item?.amenity_names,
    item?.facilities,
    item?.facility_names,
  ];
  const seen = new Set();
  const tags = [];
  sources
    .flatMap((source) => extractAmenityValues(source))
    .forEach((entry) => {
      const label = normalizeAmenityLabel(entry);
      const key = label.toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      tags.push(label);
    });
  return tags;
};
const formatShortDate = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};
const formatDateRange = (start, end) => {
  if (!start || !end) return '';
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return `${start} - ${end}`;
  }
  const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
  if (sameMonth) {
    return `${formatShortDate(start)}-${new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(endDate)}`;
  }
  return `${formatShortDate(start)}-${formatShortDate(end)}`;
};
const countNights = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86400000));
};
const toLocalIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const buildDefaultProviderDateRange = () => {
  const checkInDate = new Date();
  const checkOutDate = new Date(checkInDate);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toLocalIsoDate(checkInDate),
    checkOut: toLocalIsoDate(checkOutDate),
  };
};
const resolveListingPrice = (...values) => {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
};
const resolvePositiveNumber = (...values) => resolveListingPrice(...values);
const toNumberId = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};
const areArraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);
const areFiltersEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return false;
  return (
    left.search === right.search &&
    left.location === right.location &&
    left.bedrooms === right.bedrooms &&
    left.bathrooms === right.bathrooms &&
    left.rating === right.rating &&
    left.sort === right.sort &&
    areArraysEqual(left.categories || [], right.categories || []) &&
    areArraysEqual(left.amenities || [], right.amenities || []) &&
    areArraysEqual(left.priceRange || [], right.priceRange || [])
  );
};
const ELIVAAS_ENABLED = process.env.REACT_APP_ELIVAAS_ENABLED !== 'false';
const LISTING_PUBLIC_PAGE_BATCH_SIZE = 3;

const buildSearchSuggestions = (items = []) => {
  const suggestions = [];
  const seen = new Set();
  const add = (type, value, label, meta) => {
    const cleaned = normalizeText(value);
    if (!cleaned) return;
    const key = `${type}:${cleaned.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    suggestions.push({
      type,
      value: cleaned,
      label: normalizeText(label) || cleaned,
      meta: normalizeText(meta),
    });
  };

  items.forEach((item) => {
    const title = normalizeText(item.title || item.name);
    const city = normalizeText(item.city);
    const area = normalizeText(item.area);
    const locationMeta = [area, city].filter(Boolean).join(', ');
    if (title) add('Property', title, title, locationMeta);
    if (city) add('City', city, city, '');
    if (area) add('Location', area, area, city ? `Near ${city}` : '');
  });

  return suggestions.slice(0, 10);
};

const buildLocationSearchSuggestions = (items = []) => {
  const suggestions = [];
  const seen = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const value = normalizeText(item.name || item.label || item.city || item.display_name || item.title);
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const region = normalizeText(item.region || item.state || item.parent_name);
    const country = normalizeText(item.country || item.grandparent_name);
    suggestions.push({
      type: item.type || 'Location',
      value,
      label: value,
      meta: [region, country].filter(Boolean).join(', '),
    });
  });
  return suggestions;
};

const mergeSuggestions = (...groups) => {
  const seen = new Set();
  return groups
    .flat()
    .filter((item) => {
      const key = `${item.type}:${item.value}`.toLowerCase();
      if (!item.value || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 20);
};

const createDefaultFilters = (bounds = { min: 0, max: 50000 }) => ({
  search: '',
  location: '',
  bedrooms: '',
  bathrooms: '',
  categories: [],
  amenities: [],
  priceRange: [bounds.min, bounds.max],
  rating: null,
  sort: 'recommended',
});

const computePriceBounds = (items) => {
  const prices = items
    .map((p) => resolveListingPrice(p.price))
    .filter((p) => Number.isFinite(p) && p > 0);
  if (!prices.length) return { min: 0, max: 50000 };
  const min = Math.max(0, Math.min(...prices));
  const max = Math.max(...prices);
  return { min, max: Math.max(min + 100, max) };
};

const splitTerms = (value) =>
  normalizeText(value)
    .toLowerCase()
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const applyFilters = (items, filters, priceBounds) => {
  if (!filters) return items;
  const [minPrice, maxPrice] = filters.priceRange || [priceBounds.min, priceBounds.max];
  const hasActivePriceFilter = minPrice > priceBounds.min || maxPrice < priceBounds.max;
  const categories = new Set(
    (filters.categories || []).map((c) => normalizeText(c).toLowerCase()).filter(Boolean)
  );
  const amenities = new Set(
    (filters.amenities || []).map((a) => normalizeText(a).toLowerCase()).filter(Boolean)
  );
  const locationTerms = splitTerms(filters.location);
  const searchTerms = splitTerms(filters.search);
  const bedrooms = filters.bedrooms ? Number(filters.bedrooms) : null;
  const bathrooms = filters.bathrooms ? Number(filters.bathrooms) : null;
  const ratingMin = filters.rating ? Number(filters.rating) : null;

  return items.filter((item) => {
    const title = normalizeText(item.title).toLowerCase();
    const location = normalizeText(item.location).toLowerCase();
    if (searchTerms.length) {
      const matches = searchTerms.some((term) => title.includes(term) || location.includes(term));
      if (!matches) return false;
    }
    if (locationTerms.length) {
      const matchesLocation = locationTerms.some((term) => location.includes(term));
      if (!matchesLocation) return false;
    }
    if (categories.size) {
      const cat = normalizeText(item.category || item.propertyType).toLowerCase();
      if (!cat || !categories.has(cat)) return false;
    }
    if (amenities.size) {
      const amenityList = (item.amenities || [])
        .map((a) => normalizeText(a).toLowerCase())
        .filter(Boolean);
      const hasAll = Array.from(amenities).every((a) => amenityList.includes(a));
      if (!hasAll) return false;
    }
    if (bedrooms && Number(item.meta?.bedrooms || 0) < bedrooms) return false;
    if (bathrooms && Number(item.meta?.bathrooms || 0) < bathrooms) return false;
    if (ratingMin && Number(item.rating || 0) < ratingMin) return false;
    const price = resolveListingPrice(item.price);
    if (price === null) return !hasActivePriceFilter;
    if (price < minPrice || price > maxPrice) return false;
    return true;
  });
};

const sortProperties = (items, sortKey) => {
  const list = [...items];
  const key = (sortKey || 'recommended').toLowerCase();
  if (key === 'price-asc') {
    return list.sort(
      (a, b) =>
        (resolveListingPrice(a.price) ?? Number.MAX_SAFE_INTEGER) -
        (resolveListingPrice(b.price) ?? Number.MAX_SAFE_INTEGER)
    );
  }
  if (key === 'price-desc') {
    return list.sort(
      (a, b) =>
        (resolveListingPrice(b.price) ?? Number.NEGATIVE_INFINITY) -
        (resolveListingPrice(a.price) ?? Number.NEGATIVE_INFINITY)
    );
  }
  if (key === 'rating-desc' || key === 'top-rated') {
    return list.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
  }
  if (key === 'newest') {
    return list.sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
  }
  return list.sort((a, b) => {
    const featuredDelta = Number(b.isFeatured || 0) - Number(a.isFeatured || 0);
    if (featuredDelta) return featuredDelta;
    const ratingDelta = Number(b.rating || 0) - Number(a.rating || 0);
    if (ratingDelta) return ratingDelta;
    return Number(b.price || 0) - Number(a.price || 0);
  });
};

const PriceSlider = ({ value, onChange, min, max, formatPrice }) => {
  const [minVal, maxVal] = value || [min, max];
  const clamp = (val, low, high) => Math.min(Math.max(val, low), high);
  const gap = Math.max(1, Math.round((max - min || 1) / 20));
  const handleMin = (e) => {
    const next = clamp(Number(e.target.value), min, maxVal - gap);
    onChange([next, maxVal]);
  };
  const handleMax = (e) => {
    const next = clamp(Number(e.target.value), minVal + gap, max);
    onChange([minVal, next]);
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs font-semibold text-[#8A6428]">
        <span>{formatPrice(minVal)}</span>
        <span>{formatPrice(maxVal)}</span>
      </div>
      <div className="relative px-1">
        <input
          type="range"
          min={min}
          max={max}
          value={minVal}
          onChange={handleMin}
          className="absolute inset-0 w-full appearance-none bg-transparent accent-[#0F5C5A]"
        />
        <input
          type="range"
          min={min}
          max={max}
          value={maxVal}
          onChange={handleMax}
          className="absolute inset-0 w-full appearance-none bg-transparent accent-[#0F5C5A]"
        />
        <div className="relative h-2">
          <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-[#E7DED3]" />
          <div
            className="absolute inset-y-0 rounded-full bg-[#DDF1EE]"
            style={{
              left: `${((minVal - min) / (max - min || 1)) * 100}%`,
              right: `${(1 - (maxVal - min) / (max - min || 1)) * 100}%`,
            }}
          />
        </div>
      </div>
      <p className="text-xs text-[#667085]">
        Range: <span className="font-semibold text-[#10233D]">{formatPrice(minVal)}</span> -{' '}
        <span className="font-semibold text-[#10233D]">{formatPrice(maxVal)}</span>
      </p>
    </div>
  );
};

const DEFAULT_MAP_CENTER = [20.5937, 78.9629];

const MapBounds = ({ points = [] }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (!valid.length) {
      map.setView(DEFAULT_MAP_CENTER, 4);
      return;
    }
    const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
};

const PropertyCard = ({ property, wishlistActive, onWishlistChange, formatPrice, onHover }) => {
  const { title, location, price, images, detailPath, currency, propertyType, category } = property;
  const propertyId = property.propertyId;
  const typeLabel = (propertyType || category || 'Villa').toString().toUpperCase();
  const basePrice = resolveListingPrice(price);
  const hasPrice = basePrice !== null;
  const priceLabel = hasPrice ? formatPrice(basePrice, currency) : 'Price on request';
  const priceSuffix = normalizeText(property.priceLabel || property.displayPriceLabel);
  const priceSubtitle = hasPrice
    ? normalizeText(property.priceSubtitle) || (priceSuffix ? priceSuffix : 'Starting from / night')
    : 'check dates';
  const minStayLine =
    hasPrice &&
    Number(property.minimumStayNights || 0) > 1 &&
    property.minimumStayTotal &&
    !normalizeText(priceSubtitle).toLowerCase().includes('min.')
      ? `Min. ${property.minimumStayNights}-night stay ${formatPrice(property.minimumStayTotal, currency)}`
      : '';
  const ratingValue = Number(property.rating || 0);
  const reviewCount = Number(property.reviewsCount || 0);
  const coverImage =
    resolveMediaUrl(images?.[0], {
      width: 520,
      height: 330,
      fit: 'cover',
      quality: 72,
    }) ||
    'https://images.unsplash.com/photo-1470246973918-29a93221c455?auto=format&fit=crop&w=520&q=72';
  const amenityTags = useMemo(() => {
    const seen = new Set();
    return [...(property.tags || []), ...(property.amenities || [])]
      .map((amenity) => normalizeAmenityLabel(amenity))
      .filter((amenity) => {
        const key = amenity.toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  }, [property.tags, property.amenities]);
  const metaTags = [
    property.meta?.bedrooms ? `${property.meta.bedrooms} bed${Number(property.meta.bedrooms) === 1 ? '' : 's'}` : '',
    property.meta?.guests ? `${property.meta.guests} guest${Number(property.meta.guests) === 1 ? '' : 's'}` : '',
    property.brandBadge && property.brandBadge !== 'Homevilas' ? property.brandBadge : '',
  ]
    .map((tag) => normalizeText(tag))
    .filter(Boolean);
  const displayTags = amenityTags.length ? amenityTags : metaTags.slice(0, 2);
  const specItems = [
    property.meta?.guests
      ? {
          icon: 'bi-people',
          label: `${property.meta.guests} Guest${Number(property.meta.guests) === 1 ? '' : 's'}`,
        }
      : null,
    property.meta?.bedrooms
      ? {
          icon: 'bi-door-closed',
          label: `${property.meta.bedrooms} Bedroom${Number(property.meta.bedrooms) === 1 ? '' : 's'}`,
        }
      : null,
    property.meta?.bathrooms
      ? {
          icon: 'bi-droplet',
          label: `${property.meta.bathrooms} Bathroom${Number(property.meta.bathrooms) === 1 ? '' : 's'}`,
        }
      : null,
  ].filter(Boolean);

  const handleMouseEnter = () => {
    if (onHover) onHover(property.id);
  };
  const handleMouseLeave = () => {
    if (onHover) onHover(null);
  };
  const handleOpenDetails = (event) => {
    if (!detailPath || detailPath === '#') return;
    const interactive = event.target.closest?.('a,button,input,select,textarea,[role="button"]');
    if (interactive) return;
    window.open(detailPath, '_blank', 'noopener,noreferrer');
  };
  const handleCardKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!detailPath || detailPath === '#') return;
    event.preventDefault();
    window.open(detailPath, '_blank', 'noopener,noreferrer');
  };

  return (
    <article
      className="group flex h-full min-h-[424px] cursor-pointer flex-col overflow-hidden rounded-[20px] border border-[#E9E2D7] bg-white shadow-[0_16px_34px_rgba(16,35,61,0.08)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_22px_42px_rgba(16,35,61,0.12)]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleOpenDetails}
      onKeyDown={handleCardKeyDown}
      role={detailPath && detailPath !== '#' ? 'link' : undefined}
      tabIndex={detailPath && detailPath !== '#' ? 0 : undefined}
    >
      <div className="relative h-[264px] w-full overflow-hidden rounded-t-[20px] bg-[#F4EEE6]">
        <img
          src={coverImage}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-[1.06]"
          loading="lazy"
          decoding="async"
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#10233D]/16 via-transparent to-transparent" />

        <div className="absolute left-4 top-4 rounded-full bg-[#F4E7CF]/95 px-3 py-[6px] text-[9px] font-semibold uppercase tracking-[0.22em] text-[#8A6428] backdrop-blur-md">
          {typeLabel}
        </div>

        <div className="absolute right-3 top-3">
          {Number.isFinite(propertyId) && (
            <div className="relative">
              <WishlistToggle
                propertyId={propertyId}
                active={Boolean(wishlistActive)}
                onChange={(next) => onWishlistChange?.(propertyId, next)}
                className="!h-8 !w-8 text-[#8B97A7] hover:text-rose-600"
              />
              <i className="bi bi-heart-fill pointer-events-none absolute inset-0 flex items-center justify-center text-rose-600 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col bg-white px-5 pb-3 pt-2.5 text-left">
        <h3
          className="overflow-hidden text-[16px] font-semibold leading-[1.34] tracking-[-0.01em] text-[#10233D]"
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            minHeight: '2.65rem',
          }}
        >
          {title}
        </h3>
        <div className="mt-1 flex items-center gap-2 text-[12px] font-medium text-[#6B7280]">
          <i className="bi bi-geo-alt text-[12px] text-[#8B97A7]" aria-hidden="true" />
          <span className="truncate">{location}</span>
        </div>
        <div className="mt-2 min-h-[40px] border-t border-[#F1E9DE] pt-2">
          {specItems.length ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-[#667085]">
              {specItems.map((item) => (
                <span key={item.label} className="inline-flex items-center gap-1.5 leading-none">
                  <i className={`${item.icon} text-[12px] text-[#8B97A7]`} aria-hidden="true" />
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 overflow-hidden">
              {displayTags.map((amenity) => (
                <span
                  key={amenity}
                  className="truncate whitespace-nowrap rounded-full border border-[#E9E2D7] bg-[#FFFDFC] px-[9px] py-[3px] text-[10px] font-medium leading-none text-[#667085]"
                >
                  {amenity}
                </span>
              ))}
            </div>
          )}
        </div>
        {specItems.length && displayTags.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {displayTags.map((amenity) => (
              <span
                key={amenity}
                className="truncate whitespace-nowrap rounded-full border border-[#E9E2D7] bg-[#FFFDFC] px-[9px] py-[3px] text-[10px] font-medium leading-none text-[#667085]"
              >
                {amenity}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-auto flex items-end justify-between gap-3 pt-3">
          <div>
            <div className="text-[16px] font-bold text-[#0F5C5A]">
              {priceLabel}
            </div>
            <span className="block text-[11px] font-medium text-[#8B97A7]">
              {priceSubtitle}
            </span>
            {minStayLine ? <span className="block text-[10px] font-medium text-[#A78243]">{minStayLine}</span> : null}
            {ratingValue > 0 ? (
              <div className="mt-2">
                <RatingBadge rating={ratingValue} reviewCount={reviewCount > 0 ? reviewCount : null} compact />
              </div>
            ) : null}
          </div>
          {detailPath && detailPath !== '#' ? (
            <Link
              to={detailPath}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View details for ${title}`}
              className="rounded-full bg-[#0F5C5A] px-4.5 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white transition duration-200 hover:bg-[#0A4443]"
            >
              View
            </Link>
          ) : (
            <span className="rounded-full bg-[#0F5C5A] px-4.5 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
              View
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default function ListingPage() {
  const { formatCurrency } = useCurrency();
  const { search: searchContext } = useSearchContext();
  const formatPrice = useCallback(
    (value, sourceCurrency) => formatCurrency(value, sourceCurrency) || '--',
    [formatCurrency]
  );
  const formatMarkerPrice = useCallback(
    (value, sourceCurrency) => {
      const label = formatPrice(value, sourceCurrency);
      return label === '--' ? '\u20B9--' : label;
    },
    [formatPrice]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const hasQueryParams = searchParamsKey.length > 0;
  const hasUserFiltersRef = useRef(false);
  const allowContextFallback = !hasQueryParams && !hasUserFiltersRef.current;
  const queryDestination =
    searchParams.get('location') ||
    searchParams.get('destination') ||
    searchParams.get('city') ||
    searchParams.get('search') ||
    (allowContextFallback ? searchContext.locationLabel : '') ||
    '';
  const queryLocationId =
    searchParams.get('location_id') ||
    searchParams.get('locationId') ||
    (allowContextFallback ? searchContext.locationId : '') ||
    '';
  const queryLocationSlug =
    searchParams.get('location_slug') ||
    searchParams.get('locationSlug') ||
    (allowContextFallback ? searchContext.locationSlug : '') ||
    '';
  const queryLocationType =
    searchParams.get('location_type') ||
    searchParams.get('locationType') ||
    (allowContextFallback ? searchContext.locationType : '') ||
    '';
  const rawType = searchParams.get('type') || 'stay';
  const queryType = rawType === 'stays' ? 'stay' : rawType;
  const queryCheckIn =
    searchParams.get('checkin') ||
    searchParams.get('checkIn') ||
    (allowContextFallback ? searchContext.checkin : '') ||
    '';
  const queryCheckOut =
    searchParams.get('checkout') ||
    searchParams.get('checkOut') ||
    (allowContextFallback ? searchContext.checkout : '') ||
    '';
  const queryAdults =
    searchParams.get('adults') || (allowContextFallback ? searchContext.adults : '') || '';
  const queryChildren =
    searchParams.get('children') || (allowContextFallback ? searchContext.children : '') || '';
  const queryInfants =
    searchParams.get('infants') || (allowContextFallback ? searchContext.infants : '') || '';
  const queryGuests =
    searchParams.get('guests') ||
    (Number(queryAdults || 0) + Number(queryChildren || 0) || '') ||
    (allowContextFallback ? searchContext.guests : '') ||
    '';
  const [properties, setProperties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 50000 });
  const [filters, setFilters] = useState(() => ({
    ...createDefaultFilters({ min: 0, max: 50000 }),
    search: queryDestination,
  }));
  const [activeFilters, setActiveFilters] = useState(() => ({
    ...createDefaultFilters({ min: 0, max: 50000 }),
    search: queryDestination,
  }));
  const [showPrice, setShowPrice] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [masterLocationOptions, setMasterLocationOptions] = useState([]);
  const searchRequestRef = useRef(0);
  const searchAbortRef = useRef(null);
  const searchCommitTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const fetchRequestRef = useRef(0);
  const priceRef = useRef(null);
  const [wishlistIds, setWishlistIds] = useState(() => new Set());
  const [viewMode, setViewMode] = useState('grid');
  const gridColumns = viewMode === 'map' ? 2 : 3;
  const mapMode = viewMode === 'map';
  const [hoveredId, setHoveredId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    let active = true;
    fetchJson('/api/search/popular?limit=80')
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res?.items) ? res.items : Array.isArray(res?.data) ? res.data : [];
        const options = rows
          .map((item) => resolveStateName(item, item.city || item.name || item.display_name))
          .filter(Boolean);
        setMasterLocationOptions(Array.from(new Set(options)).sort());
      })
      .catch(() => {
        if (active) setMasterLocationOptions([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const detailSuffix = useMemo(() => {
    const params = new URLSearchParams();
    if (queryType) params.set('type', queryType);
    if (queryDestination) params.set('location', queryDestination);
    if (queryCheckIn) params.set('checkin', queryCheckIn);
    if (queryCheckOut) params.set('checkout', queryCheckOut);
    if (queryAdults) params.set('adults', String(queryAdults));
    if (queryChildren) params.set('children', String(queryChildren));
    if (queryInfants) params.set('infants', String(queryInfants));
    return params.toString() ? `?${params.toString()}` : '';
  }, [
    queryType,
    queryDestination,
    queryCheckIn,
    queryCheckOut,
    queryAdults,
    queryChildren,
    queryInfants,
  ]);

  const normalizeProperty = (item) => {
    const slug = item.slug || item.id || item._id;
    const providerPropertyId = normalizeText(
      extractElivaasProviderId(item) || item.provider_property_id || item.providerPropertyId || ''
    );
    const defaultProviderQuote =
      (Array.isArray(item.quotes) && item.quotes.find((quote) => quote)) ||
      null;
    const isExternal =
      item.is_external === true ||
      item.source === 'api' ||
      item.provider_code === 'elivaas' ||
      Boolean(providerPropertyId);
    const rawCity =
      item.city ||
      item.location?.city ||
      item.location?.name ||
      item.location?.label ||
      item.location?.title ||
      (typeof item.location === 'string' ? item.location : '');
    const rawArea =
      item.area || item.state || item.location?.state || item.location?.region || item.location?.area;
    const locationParts = [rawCity, rawArea].map(normalizeText).filter(Boolean);
    const amenityList = collectAmenityTags(item);
    const images =
      (Array.isArray(item.images) && item.images.length && item.images) ||
      (Array.isArray(item.gallery) && item.gallery.length && item.gallery) ||
      (Array.isArray(item.media) && item.media.length && item.media) ||
      [
        item.featured_image,
        item.coverImage,
        item.cover_image,
        item.image_url,
        item.imageUrl,
        item.thumbnail,
      ].filter(Boolean);
    const title =
      publicBrandText(normalizeText(item.title || item.name || item.property_name || item.propertyName)) ||
      normalizeText(locationParts.join(', ')) ||
      'Homevilas Stay';
    const propertyId = isExternal ? null : toNumberId(item.id || item.property_id || item.propertyId);
    const selectedNights = countNights(queryCheckIn, queryCheckOut);
    const normalizedNightlyPrice = resolvePositiveNumber(
      item.price_per_night,
      item.pricePerNight,
      item.sell_price_per_night,
      item.sellPricePerNight,
      item.display_price,
      item.final_per_night_display_price,
      item.display_sell_price,
      item.price,
      item.basePricePerNight,
      item.base_price_per_night,
      item.price_from,
      item.total_for_dates,
      item.startingPrice,
      item.starting_price,
      item.base_price,
      item.provider_base_price,
      item.minimumPrice,
      item.priceAmount
    );
    const normalizedTotalStayAmount = resolvePositiveNumber(
      item.total_for_dates,
      item.total_stay_amount,
      item.minimum_stay_total
    );
    const resolvedListingCardPrice =
      selectedNights > 0 && normalizedTotalStayAmount
        ? normalizedTotalStayAmount
        : normalizedNightlyPrice;
    const displayPriceLabel = normalizeText(item.display_price_label || item.priceLabel);
    const minimumStayNights = Number(item.minimum_stay_nights || item.minimumStayNights || 0) || null;
    const priceSubtitle =
      selectedNights > 0 && normalizedTotalStayAmount && normalizedNightlyPrice
        ? `${selectedNights} night${selectedNights === 1 ? '' : 's'} · ${formatPrice(normalizedNightlyPrice, item.currency || item.base_currency || 'INR')} avg. / night`
        : displayPriceLabel ||
          normalizeText(item.price_subtitle || item.priceSubtitle) ||
          (minimumStayNights > 1 ? `Min. ${minimumStayNights}-night stay` : 'Starting from / night');
    const externalDetailParams = new URLSearchParams();
    if (queryType) externalDetailParams.set('type', queryType);
    if (queryDestination) externalDetailParams.set('location', queryDestination);
    if (queryCheckIn) externalDetailParams.set('checkIn', queryCheckIn);
    if (queryCheckOut) externalDetailParams.set('checkOut', queryCheckOut);
    if (queryAdults) externalDetailParams.set('adults', String(queryAdults));
    if (queryChildren) externalDetailParams.set('children', String(queryChildren));
    if (queryInfants) externalDetailParams.set('infants', String(queryInfants));
    if (title) externalDetailParams.set('propertyName', title);
    if (rawCity) externalDetailParams.set('city', rawCity);
    if (rawArea) externalDetailParams.set('area', rawArea);
    if (locationParts.length) externalDetailParams.set('locationLabel', locationParts.join(', '));
    if (images[0]) externalDetailParams.set('image', images[0]);
    if (defaultProviderQuote?.ratePlanCode) externalDetailParams.set('ratePlanCode', defaultProviderQuote.ratePlanCode);
    if (defaultProviderQuote?.quoteId) externalDetailParams.set('quoteId', defaultProviderQuote.quoteId);
    if (resolvedListingCardPrice) {
      externalDetailParams.set('price', String(resolvedListingCardPrice));
    }
    const externalDetailSuffix = externalDetailParams.toString()
      ? `?${externalDetailParams.toString()}`
      : '';
    const providerDetailPath = buildElivaasDetailPath({ ...item, provider_property_id: providerPropertyId });
    const detailPath = isExternal
      ? ELIVAAS_ENABLED && providerPropertyId
        ? `${providerDetailPath}${externalDetailSuffix}`
        : '#'
      : slug
      ? `/listings/${slug}${detailSuffix}`
      : '#';

    const badges = [];
    if (item.featured) badges.push('Featured');
    if (item.trending) badges.push('Trending');
    if (item.pet_friendly) badges.push('Pet Friendly');
    if (Array.isArray(item.badges)) badges.push(...item.badges);

    return {
      id: item.id || slug || Math.random().toString(36).slice(2),
      propertyId,
      providerPropertyId: providerPropertyId || null,
      isExternal,
      detailPath,
      title,
      location: locationParts.join(', ') || 'India',
      category: normalizeText(
        item.category?.name ||
          item.category?.title ||
          item.category?.slug ||
          item.category_name ||
          item.type ||
          item.property_type ||
          item.listing_type ||
          ''
      ),
      propertyType: normalizeText(item.propertyType || item.property_type || item.type || ''),
      rating:
        item.rating !== undefined
          ? Number(item.rating)
          : item.avg_rating !== undefined
          ? Number(item.avg_rating)
          : null,
      reviewsCount: item.reviewsCount ?? item.reviews_count ?? item.review_count ?? 0,
      isFeatured: item.isFeatured ?? item.is_featured ?? false,
      badges,
      brandBadge:
        isExternal
          ? 'Homevilas'
          : publicBrandText(item.provider_name || item.provider_code) ||
            item.brand ||
            item.channel ||
            'Homevilas',
      meta: {
        guests: item.max_guests || item.guests,
        bedrooms: item.bedrooms || item.beds,
        bathrooms: item.bathrooms || item.baths,
      },
      tags: amenityList.slice(0, 3),
      amenities: amenityList.slice(0, 6),
      currency: item.currency || item.base_currency || 'INR',
      partialPaymentAvailable: Boolean(item.partialPaymentAvailable ?? item.partial_payment_available),
      partialPaymentPercentage:
        item.partialPaymentPercentage ??
        item.partial_payment_percentage ??
        null,
      price: resolvedListingCardPrice,
      nightlyPrice: normalizedNightlyPrice,
      totalStayAmount: normalizedTotalStayAmount,
      minimumStayTotal: resolvePositiveNumber(item.minimum_stay_total, item.total_stay_amount),
      minimumStayNights,
      displayPriceUnit: item.display_price_unit || item.displayPriceUnit || null,
      displayPriceLabel,
      priceLabel: displayPriceLabel,
      priceSubtitle,
      lat:
        item.lat ??
        item.latitude ??
        item.location?.lat ??
        item.location?.latitude ??
        null,
      lng:
        item.lng ??
        item.longitude ??
        item.location?.lng ??
        item.location?.longitude ??
        null,
      createdAt: item.createdAt || item.created_at || null,
      images:
        images.length > 0
          ? images
          : [
              'https://images.unsplash.com/photo-1470246973918-29a93221c455?auto=format&fit=crop&w=1200&q=80',
              'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80',
              'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80',
            ],
    };
  };

  const buildPriceIcon = useCallback(
    (label, active) =>
      L.divIcon({
        className: `hv-price-marker ${active ? 'hv-price-marker--active' : ''}`,
        html: `<div class="hv-price-marker__bubble">${label}</div><div class="hv-price-marker__tip"></div>`,
        iconSize: [10, 10],
        iconAnchor: [16, 34],
        popupAnchor: [0, -34],
      }),
    []
  );

  const syncFiltersToParams = useCallback(
    (nextFilters, { replace = true } = {}) => {
      const params = new URLSearchParams(searchParamsKey);
      const locationValue = normalizeText(nextFilters.location || nextFilters.search);
      if (locationValue) {
        params.set('location', locationValue);
        params.set('city', locationValue);
        params.delete('destination');
        params.delete('search');
      } else {
        params.delete('location');
        params.delete('destination');
        params.delete('city');
        params.delete('search');
      }
      if (locationValue.toLowerCase() !== normalizeText(queryDestination).toLowerCase()) {
        params.delete('location_id');
        params.delete('locationId');
        params.delete('location_slug');
        params.delete('locationSlug');
        params.delete('location_type');
        params.delete('locationType');
      }

      const categoryValue = nextFilters.categories?.[0] || '';
      if (categoryValue) params.set('category', categoryValue);
      else params.delete('category');

      if (nextFilters.rating) params.set('rating', String(nextFilters.rating));
      else params.delete('rating');

      if (nextFilters.bedrooms) params.set('beds', String(nextFilters.bedrooms));
      else params.delete('beds');

      params.delete('baths');
      params.delete('amenities');

      const [minPriceRaw, maxPriceRaw] = nextFilters.priceRange || priceBounds;
      const minPrice = Number.isFinite(minPriceRaw) ? minPriceRaw : priceBounds.min;
      const maxPrice = Number.isFinite(maxPriceRaw) ? maxPriceRaw : priceBounds.max;
      if (minPrice !== priceBounds.min && !(minPrice === 0 && priceBounds.min > 0)) {
        params.set('minPrice', String(minPrice));
      } else {
        params.delete('minPrice');
      }
      if (maxPrice !== priceBounds.max && !(maxPrice === 0 && priceBounds.max > 0)) {
        params.set('maxPrice', String(maxPrice));
      } else {
        params.delete('maxPrice');
      }

      if (nextFilters.sort && nextFilters.sort !== 'recommended') params.set('sort', nextFilters.sort);
      else params.delete('sort');

      const nextString = params.toString();
      if (nextString === searchParamsKey) return;
      setSearchParams(params, { replace });
    },
    [searchParamsKey, setSearchParams, priceBounds, queryDestination]
  );

  const commitFilters = useCallback(
    (updater) => {
      setFilters((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setActiveFilters(next);
        syncFiltersToParams(next, { replace: true });
        hasUserFiltersRef.current = true;
        return next;
      });
    },
    [syncFiltersToParams]
  );

  const parsedFilters = useMemo(() => {
    const base = createDefaultFilters(priceBounds);
    const params = new URLSearchParams(searchParamsKey);
    const categoryValue = params.get('category') || '';
    const ratingValue = Number(params.get('rating'));
    const bedsValue = params.get('beds') || '';
    const minPriceRaw = params.get('minPrice');
    const maxPriceRaw = params.get('maxPrice');
    const minPriceValue = minPriceRaw !== null ? Number(minPriceRaw) : Number.NaN;
    const maxPriceValue = maxPriceRaw !== null ? Number(maxPriceRaw) : Number.NaN;
    const resolvedMinPrice =
      Number.isFinite(minPriceValue) && !(minPriceValue === 0 && priceBounds.min > 0)
        ? minPriceValue
        : priceBounds.min;
    const resolvedMaxPrice =
      Number.isFinite(maxPriceValue) && !(maxPriceValue === 0 && priceBounds.max > 0)
        ? maxPriceValue
        : priceBounds.max;
    const sortValue = params.get('sort') || 'recommended';

    return {
      ...base,
      search: queryDestination || '',
      location: '',
      categories: categoryValue ? [categoryValue] : [],
      rating: Number.isFinite(ratingValue) ? ratingValue : null,
      bedrooms: bedsValue,
      bathrooms: '',
      amenities: [],
      priceRange: [resolvedMinPrice, resolvedMaxPrice],
      sort: sortValue,
    };
  }, [searchParamsKey, queryDestination, priceBounds]);

  useEffect(() => {
    setFilters((prev) => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters));
    setActiveFilters((prev) => (areFiltersEqual(prev, parsedFilters) ? prev : parsedFilters));
  }, [parsedFilters]);

  const applySearchTerm = (value) => {
    const cleaned = normalizeText(value);
    commitFilters((prev) => ({ ...prev, search: cleaned, location: '' }));
  };

  const fetchProperties = async ({
    searchTerm = '',
    locationId = '',
    locationSlug = '',
    locationType = '',
    type = '',
    checkin = '',
    checkout = '',
    adults = '',
    children = '',
    infants = '',
    guests = '',
  } = {}) => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const requestId = ++fetchRequestRef.current;
    const startedAt = performance.now();
    setLoading(true);
    setError('');
    const applyResponse = (res) => {
      if (requestId !== fetchRequestRef.current) return;
      const items = res?.listings || res?.data || res?.items || (Array.isArray(res) ? res : []);
      const normalized = items
        .map(normalizeProperty)
        .filter((item) => ELIVAAS_ENABLED || !item.isExternal);
      const bounds = computePriceBounds(normalized);
      setProperties(normalized);
      setPriceBounds(bounds);
      const nextTotal =
        ELIVAAS_ENABLED
          ? res?.total ??
            res?.pagination?.total ??
            res?.meta?.total ??
            (Array.isArray(items) ? items.length : 0)
          : normalized.length;
      setTotalCount(nextTotal);
      setFilters((prev) => {
        const shouldReset =
          !prev.priceRange || (prev.priceRange[0] === 0 && prev.priceRange[1] === 50000);
        return shouldReset ? { ...prev, priceRange: [bounds.min, bounds.max] } : prev;
      });
      setActiveFilters((prev) => {
        const shouldReset =
          !prev.priceRange || (prev.priceRange[0] === 0 && prev.priceRange[1] === 50000);
        return shouldReset ? { ...prev, priceRange: [bounds.min, bounds.max] } : prev;
      });
    };
    const extractItems = (payload) =>
      Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
    const mergePublicResponses = (...responses) => {
      const idMap = new Map();
      let total = 0;
      responses.filter(Boolean).forEach((response, responseIndex) => {
        const entries = extractItems(response);
        total += Number(
          response?.pagination?.total ??
            response?.meta?.total ??
            response?.total ??
            entries.length ??
            0
        );
        entries.forEach((entry, index) => {
          const sourceKey = entry?.source || (entry?.is_external ? 'api' : 'direct');
          const itemKey = String(
            entry?.provider_property_id ||
              entry?.providerPropertyId ||
              entry?.id ||
              entry?.slug ||
              `${responseIndex}-${index}-${entry?.title || entry?.name || 'row'}`
          );
          const key = `${sourceKey}:${itemKey}`;
          idMap.set(key, entry);
        });
      });
      const mergedItems = Array.from(idMap.values());
      const first = responses.find(Boolean) || {};
      const mergedTotal = mergedItems.length || total;
      return {
        ...first,
        items: mergedItems,
        data: mergedItems,
        total: mergedTotal,
        pagination: {
          ...(first?.pagination || {}),
          total: mergedTotal,
          limit: mergedItems.length,
          totalPages: 1,
        },
        meta: {
          ...(first?.meta || {}),
          total: mergedTotal,
        },
      };
    };

    const shouldEnrichProviderPrice = (entry = {}) => {
      const providerId = extractElivaasProviderId(entry) || entry?.provider_property_id || entry?.providerPropertyId;
      if (!providerId) return false;
      const price = Number(entry.price_per_night || entry.pricePerNight || entry.sell_price_per_night || entry.sellPricePerNight || 0);
      if (!Number.isFinite(price) || price < 100000) return false;
      const tags = Array.isArray(entry.tags) ? entry.tags : [];
      return tags.length === 0 || price === 108000 || price === 120000 || price === 240000;
    };

    const enrichProviderFallbackPrices = async (response, options = {}) => {
      const entries = extractItems(response);
      const enriched = [...entries];
      const candidates = enriched
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => shouldEnrichProviderPrice(entry))
        .slice(0, 36);
      if (!candidates.length) return response;

      await Promise.all(
        candidates.map(async ({ entry, index }) => {
          const providerId = extractElivaasProviderId(entry) || entry?.provider_property_id || entry?.providerPropertyId;
          try {
            const detail = await fetchJson(
              `/api/providers/elivaas/properties/${encodeURIComponent(providerId)}?${new URLSearchParams({
                checkIn: options.checkIn || '',
                checkOut: options.checkOut || '',
                adults: String(options.adults || ''),
                children: String(options.children || ''),
              }).toString()}`,
              { signal: controller.signal }
            );
            const source = detail?.data || detail?.item || detail || {};
            const nextPrice = resolvePositiveNumber(
              source.price_per_night,
              source.pricePerNight,
              source.sell_price_per_night,
              source.sellPricePerNight,
              source.display_sell_price,
              source.final_per_night_display_price,
              source.base_price_per_night,
              source.provider_base_price
            );
            if (!nextPrice) return;
            enriched[index] = {
              ...entry,
              ...source,
              id: entry.id,
              slug: entry.slug || source.slug || providerId,
              source: entry.source || 'api',
              is_external: entry.is_external ?? true,
              provider_property_id: providerId,
              price_per_night: nextPrice,
              pricePerNight: nextPrice,
              sell_price_per_night: nextPrice,
              display_sell_price: nextPrice,
            };
          } catch (_) {
            // Keep the listing row if provider detail is unavailable.
          }
        })
      );

      return {
        ...response,
        items: enriched,
        data: enriched,
      };
    };

    const fetchAllPublicPages = async (baseParams, { onPartial } = {}) => {
      const paramsFirst = new URLSearchParams(baseParams);
      paramsFirst.set('page', '1');
      const first = await fetchJson(`/api/public/properties?${paramsFirst.toString()}`, {
        signal: controller.signal,
      });
      const firstItems = extractItems(first);
      const idMap = new Map();
      const appendItems = (entries, pageIndex) => {
        (Array.isArray(entries) ? entries : []).forEach((entry, index) => {
          const key = String(entry?.id || entry?.slug || `${pageIndex}-${index}-${entry?.title || 'row'}`);
          if (!idMap.has(key)) idMap.set(key, entry);
        });
      };
      const buildMergedResponse = () => {
        const mergedItems = Array.from(idMap.values());
        return {
          ...first,
          items: mergedItems,
          data: mergedItems,
          pagination: {
            ...(first?.pagination || {}),
            total: Number(first?.pagination?.total ?? first?.total ?? mergedItems.length),
            totalPages: Math.max(1, totalPages),
          },
          total: Number(first?.total ?? first?.pagination?.total ?? mergedItems.length),
        };
      };
      appendItems(firstItems, 1);
      const totalPages = Number(first?.pagination?.totalPages || 1);
      if (typeof onPartial === 'function') {
        onPartial(buildMergedResponse());
      }
      for (let pageStart = 2; pageStart <= totalPages; pageStart += LISTING_PUBLIC_PAGE_BATCH_SIZE) {
        const batchPromises = [];
        for (
          let pageIndex = pageStart;
          pageIndex < pageStart + LISTING_PUBLIC_PAGE_BATCH_SIZE && pageIndex <= totalPages;
          pageIndex += 1
        ) {
          const paramsPage = new URLSearchParams(baseParams);
          paramsPage.set('page', String(pageIndex));
          batchPromises.push(
            fetchJson(`/api/public/properties?${paramsPage.toString()}`, {
              signal: controller.signal,
            })
          );
        }
        const batchResponses = await Promise.all(batchPromises);
        batchResponses.forEach((next, batchIndex) => {
          appendItems(extractItems(next), pageStart + batchIndex);
        });
      }
      return buildMergedResponse();
    };

    try {
      const normalizedType = (type || 'stay').toString().toLowerCase();
      const serviceType = normalizedType === 'stay' ? 'stays' : normalizedType;
      const totalGuests = Number(guests || 0) || Number(adults || 0) + Number(children || 0) || 0;
      const hasDates = Boolean(checkin && checkout);
      const providerDateRange = hasDates
        ? { checkIn: checkin, checkOut: checkout }
        : buildDefaultProviderDateRange();
      const rawSearch = normalizeText(searchTerm);
      const searchParts = splitTerms(rawSearch);
      const normalizedSearch = searchParts[0] || rawSearch;
      const fetchProviderCatalogSnapshot = async () => {
        if (!ELIVAAS_ENABLED) return null;
        const catalogParams = new URLSearchParams();
        catalogParams.set('provider', 'elivaas');
        catalogParams.set('limit', '500');
        if (normalizedSearch) {
          catalogParams.set('location', normalizedSearch);
          catalogParams.set('city', normalizedSearch);
        }
        if (Number(adults) > 0) catalogParams.set('adults', String(Number(adults)));
        if (Number(children) > 0) catalogParams.set('children', String(Number(children)));
        if (!catalogParams.get('adults') && totalGuests > 0) {
          catalogParams.set('adults', String(totalGuests));
        }
        let payload = null;
        try {
          const response = await fetch(`/provider-catalog/elivaas.json?t=${Date.now()}`, {
            cache: 'no-store',
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Provider catalog unavailable');
          payload = await response.json();
        } catch (snapshotErr) {
          debugLog('provider-catalog:static-failed', snapshotErr?.message || snapshotErr);
          payload = await fetchJson(`/api/public/provider-catalog?${catalogParams.toString()}`, {
            signal: controller.signal,
          });
        }
        const locationTerms = splitTerms(normalizedSearch);
        const items = extractItems(payload)
          .filter((item) => {
            if (totalGuests > 0 && Number(item.max_guests || 0) > 0 && Number(item.max_guests || 0) < totalGuests) {
              return false;
            }
            if (!locationTerms.length) return true;
            const haystack = [
              item.title,
              item.name,
              item.city,
              item.area,
              item.state,
              item.country,
              item.location,
            ]
              .map((value) => normalizeText(value).toLowerCase())
              .filter(Boolean)
              .join(' ');
            return locationTerms.some((term) => haystack.includes(term));
          });
        return {
          ...payload,
          items,
          data: items,
          total: items.length,
          pagination: {
            total: items.length,
            page: 1,
            limit: items.length,
            totalPages: 1,
          },
          meta: { total: items.length, source: 'provider-catalog' },
        };
      };
      try {

        const publicParams = new URLSearchParams();
        if (normalizedSearch) {
          publicParams.set('location', normalizedSearch);
          publicParams.set('city', normalizedSearch);
        }
        if (hasDates) {
          publicParams.set('checkIn', checkin);
          publicParams.set('checkOut', checkout);
        }
        if (Number(adults) > 0) publicParams.set('adults', String(Number(adults)));
        if (Number(children) > 0) publicParams.set('children', String(Number(children)));
        if (!publicParams.get('adults') && totalGuests > 0) {
          publicParams.set('adults', String(totalGuests));
        }
        publicParams.set('page', '1');
        publicParams.set('limit', '200');
        if (locationId) publicParams.set('locationId', locationId);
        if (locationSlug) publicParams.set('locationSlug', locationSlug);
        if (locationType) publicParams.set('locationType', locationType);
        if (serviceType && serviceType !== 'stays') publicParams.set('serviceType', serviceType);
        publicParams.set('source', 'direct');
        let snapshotRes = null;
        try {
          snapshotRes = await fetchProviderCatalogSnapshot();
          if (extractItems(snapshotRes).length > 0 && normalizedSearch) {
            debugLog('provider-catalog:loaded', {
              ms: Math.round(performance.now() - startedAt),
              count: extractItems(snapshotRes).length,
            });
          }
        } catch (snapshotErr) {
          debugLog('provider-catalog:snapshot-failed', snapshotErr?.message || snapshotErr);
        }
        const applyDirectPartial = (response) => {
          if (!ELIVAAS_ENABLED || extractItems(response).length > 0 || !extractItems(snapshotRes).length) {
            applyResponse(response);
          }
        };
        const directRes = await fetchAllPublicPages(publicParams, { onPartial: applyDirectPartial });
        if (!ELIVAAS_ENABLED) {
          applyResponse(directRes);
          return;
        }
        let publicRes = directRes;
        if (extractItems(publicRes).length > 0) {
          applyResponse(publicRes);
        }
        const apiParams = new URLSearchParams(publicParams);
        apiParams.set('source', 'api');
        apiParams.set('checkIn', providerDateRange.checkIn);
        apiParams.set('checkOut', providerDateRange.checkOut);
        try {
          const apiRes = await fetchAllPublicPages(apiParams);
          publicRes = mergePublicResponses(snapshotRes, directRes, apiRes);
          publicRes = await enrichProviderFallbackPrices(publicRes, {
            checkIn: providerDateRange.checkIn,
            checkOut: providerDateRange.checkOut,
            adults: Number(adults) || Number(totalGuests) || 2,
            children: Number(children) || 0,
          });
          applyResponse(publicRes);
        } catch (apiErr) {
          applyResponse(publicRes);
          debugLog('public-properties:api-merge-failed', apiErr?.message || apiErr);
        }
        debugLog('public-properties:success', {
          ms: Math.round(performance.now() - startedAt),
          count:
            publicRes?.total ??
            publicRes?.pagination?.total ??
            publicRes?.meta?.total ??
            publicRes?.items?.length ??
            publicRes?.data?.length ??
            (Array.isArray(publicRes) ? publicRes.length : undefined),
        });
        return;
      } catch (_) {
        // Fall back to legacy search endpoint.
      }

      try {
        const payload = {
          serviceType,
          locationId: locationId || undefined,
          locationType: locationType || undefined,
          locationSlug: locationSlug || undefined,
          location: normalizedSearch || undefined,
          checkIn: hasDates ? checkin : undefined,
          checkOut: hasDates ? checkout : undefined,
          guests: totalGuests > 0 ? totalGuests : undefined,
        };
        debugLog('search:start', payload);
        const res = await fetchJson('/api/search', {
          method: 'POST',
          body: payload,
          signal: controller.signal,
        });
        applyResponse(res);
        debugLog('search:success', {
          ms: Math.round(performance.now() - startedAt),
          count: res?.total ?? res?.items?.length ?? res?.data?.length ?? res?.listings?.length,
        });
        return;
      } catch (err) {
        if (controller.signal.aborted) return;
        const params = new URLSearchParams();
        if (normalizedSearch) {
          params.set('location', normalizedSearch);
          params.set('city', normalizedSearch);
        }
        if (totalGuests > 0) params.set('guests', String(totalGuests));
        params.set('source', 'direct');
        try {
          const fallback = await fetchAllPublicPages(params, { onPartial: applyResponse });
          applyResponse(fallback);
          debugLog('search:fallback', {
            ms: Math.round(performance.now() - startedAt),
            count:
              fallback?.total ??
              fallback?.items?.length ??
              fallback?.data?.length ??
              (Array.isArray(fallback) ? fallback.length : undefined),
          });
          return;
        } catch (fallbackErr) {
          throw err;
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err?.message || 'Failed to load properties');
    } finally {
      if (requestId === fetchRequestRef.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProperties({
      searchTerm: queryDestination,
      locationId: queryLocationId,
      locationSlug: queryLocationSlug,
      locationType: queryLocationType,
      type: queryType,
      checkin: queryCheckIn,
      checkout: queryCheckOut,
      adults: queryAdults,
      children: queryChildren,
      infants: queryInfants,
      guests: queryGuests,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryDestination,
    queryLocationId,
    queryLocationSlug,
    queryLocationType,
    queryType,
    queryCheckIn,
    queryCheckOut,
    queryAdults,
    queryChildren,
    queryInfants,
    queryGuests,
  ]);

  useEffect(() => {
    const token =
      getSessionItem('customerAccessToken') ||
      getLocalItem('customerAccessToken');
    if (!token) return;
    let active = true;
    fetchGuestWishlist()
      .then((res) => {
        if (!active) return;
        const ids = new Set(
          (res.data || [])
            .map((item) => toNumberId(item.property_id || item.id))
            .filter((id) => Number.isFinite(id))
        );
        setWishlistIds(ids);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleClick = (event) => {
      if (priceRef.current && !priceRef.current.contains(event.target)) {
        setShowPrice(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    return () => {
      if (searchCommitTimerRef.current) {
        window.clearTimeout(searchCommitTimerRef.current);
      }
      fetchAbortRef.current?.abort();
      searchAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!searchFocused) return;
    const query = normalizeText(filters.search);
    if (query.length < 2) {
      searchAbortRef.current?.abort();
      setSearchSuggestions([]);
      setSearchLoading(false);
      return;
    }
    const requestId = ++searchRequestRef.current;
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      const startedAt = performance.now();
      try {
        const params = new URLSearchParams({ location: query, limit: '12', source: 'direct' });
        const [res, locationRes] = await Promise.allSettled([
          fetchJson(`/api/public/properties?${params.toString()}`, {
            signal: controller.signal,
          }),
          fetchJson(`/api/search/locations?q=${encodeURIComponent(query)}&limit=50`, {
            signal: controller.signal,
          }),
        ]);
        const propertyRes = res.status === 'fulfilled' ? res.value : null;
        const items = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(propertyRes?.items)
          ? propertyRes.items
          : Array.isArray(propertyRes?.data)
          ? propertyRes.data
          : Array.isArray(propertyRes)
          ? propertyRes
          : [];
        const locationData = locationRes.status === 'fulfilled' ? locationRes.value : null;
        const locationItems = Array.isArray(locationData?.items)
          ? locationData.items
          : Array.isArray(locationData?.data)
          ? locationData.data
          : Array.isArray(locationData)
          ? locationData
          : [];
        if (searchRequestRef.current !== requestId) return;
        setSearchSuggestions(mergeSuggestions(buildLocationSearchSuggestions(locationItems), buildSearchSuggestions(items)));
        debugLog('suggestions', {
          query,
          count: items.length,
          ms: Math.round(performance.now() - startedAt),
        });
      } catch (_) {
        if (controller.signal.aborted) return;
        if (searchRequestRef.current !== requestId) return;
        setSearchSuggestions([]);
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [filters.search, searchFocused]);

  const shouldShowSuggestions = searchFocused && normalizeText(filters.search).length >= 2;

  const { locationOptions, categoryOptions } = useMemo(() => {
    const locations = new Set();
    const categories = new Set();
    const amenityCounts = new Map();
    ['Villa', 'Apartment', 'Cottage', 'Homestay'].forEach((type) => categories.add(type));

    properties.forEach((p) => {
      const loc = normalizeText(p.location).split(',')[0].trim();
      if (loc) locations.add(loc);
      const category = normalizeText(p.category);
      if (category) categories.add(category);
      (p.amenities || []).forEach((a) => {
        if (!a) return;
        const key = normalizeText(a);
        if (!key) return;
        amenityCounts.set(key, (amenityCounts.get(key) || 0) + 1);
      });
    });

    return {
      locationOptions: Array.from(new Set([...masterLocationOptions, ...locations])).sort(),
      categoryOptions: Array.from(categories).sort(),
      amenityOptions: Array.from(amenityCounts.entries())
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [masterLocationOptions, properties]);

  const filteredProperties = useMemo(() => {
    const base = applyFilters(properties, activeFilters, priceBounds);
    return sortProperties(base, activeFilters.sort);
  }, [properties, activeFilters, priceBounds]);
  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / pageSize));
  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProperties.slice(start, start + pageSize);
  }, [filteredProperties, currentPage, pageSize]);
  const markerItems = useMemo(
    () =>
      paginatedProperties
        .map((item) => ({
          ...item,
          lat: Number(item.lat),
          lng: Number(item.lng),
          priceLabel: formatMarkerPrice(item.price, item.currency),
        }))
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng)),
    [paginatedProperties, formatMarkerPrice]
  );
  const activeFilterChips = useMemo(() => {
    const chips = [];
    const locationLabel = normalizeText(activeFilters.search || activeFilters.location || queryDestination);
    if (locationLabel) chips.push({ key: 'location', label: locationLabel });

    if (queryCheckIn && queryCheckOut) {
      chips.push({ key: 'dates', label: formatDateRange(queryCheckIn, queryCheckOut) });
    }

    const guestCount =
      Number(queryGuests || 0) || Number(queryAdults || 0) + Number(queryChildren || 0);
    if (guestCount) {
      chips.push({ key: 'guests', label: `${guestCount} Guest${guestCount > 1 ? 's' : ''}` });
    }

    if (activeFilters.categories?.length) {
      chips.push({ key: 'category', label: activeFilters.categories[0] });
    }
    if (activeFilters.rating) {
      chips.push({ key: 'rating', label: `${activeFilters.rating}+ rating` });
    }
    if (activeFilters.bedrooms) {
      chips.push({ key: 'beds', label: `${activeFilters.bedrooms}+ beds` });
    }
    const [minPrice, maxPrice] = activeFilters.priceRange || priceBounds;
    if (minPrice !== priceBounds.min || maxPrice !== priceBounds.max) {
      chips.push({
        key: 'price',
        label: `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`,
      });
    }
    return chips;
  }, [
    activeFilters,
    queryDestination,
    queryCheckIn,
    queryCheckOut,
    queryGuests,
    queryAdults,
    queryChildren,
    priceBounds,
    formatPrice,
  ]);

  const clearFilter = useCallback(
    (key) => {
      if (key === 'dates') {
        const next = new URLSearchParams(searchParams);
        next.delete('checkin');
        next.delete('checkout');
        next.delete('checkIn');
        next.delete('checkOut');
        setSearchParams(next, { replace: true });
        return;
      }
      if (key === 'guests') {
        const next = new URLSearchParams(searchParams);
        next.delete('adults');
        next.delete('children');
        next.delete('infants');
        next.delete('guests');
        setSearchParams(next, { replace: true });
        return;
      }
      if (key === 'price') {
        commitFilters((prev) => ({ ...prev, priceRange: [priceBounds.min, priceBounds.max] }));
        return;
      }
      if (key === 'category') {
        commitFilters((prev) => ({ ...prev, categories: [] }));
        return;
      }
      if (key === 'rating') {
        commitFilters((prev) => ({ ...prev, rating: null }));
        return;
      }
      if (key === 'beds') {
        commitFilters((prev) => ({ ...prev, bedrooms: '' }));
        return;
      }
      if (key === 'location') {
        commitFilters((prev) => ({ ...prev, search: '', location: '' }));
      }
    },
    [commitFilters, priceBounds, searchParams, setSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    const resetFilters = createDefaultFilters(priceBounds);
    setFilters(resetFilters);
    setActiveFilters(resetFilters);
    const next = new URLSearchParams(searchParams);
    ['location', 'destination', 'city', 'search', 'category', 'rating', 'beds', 'baths', 'amenities', 'minPrice', 'maxPrice', 'sort'].forEach(
      (param) => next.delete(param)
    );
    next.delete('checkin');
    next.delete('checkout');
    next.delete('checkIn');
    next.delete('checkOut');
    next.delete('adults');
    next.delete('children');
    next.delete('infants');
    next.delete('guests');
    setSearchParams(next, { replace: true });
  }, [priceBounds, searchParams, setSearchParams]);

  const hasFiltersApplied = activeFilterChips.length > 0;
  const headerSubtitle = queryDestination
    ? `${totalCount} properties in ${queryDestination}`
    : `${totalCount} properties across India`;
  const showingCount = filteredProperties.length;
  const totalLabelCount = totalCount || filteredProperties.length;
  const gridClass = `grid auto-rows-fr gap-5 sm:grid-cols-2 ${
    gridColumns === 3 ? 'lg:grid-cols-3' : ''
  }`;
  const showSkeleton = loading && filteredProperties.length === 0;
  const showUpdating = loading && properties.length > 0;
  const emptyLocationLabel = normalizeText(activeFilters.search || activeFilters.location || queryDestination);
  const emptyResultsMessage = emptyLocationLabel
    ? `No property found in ${emptyLocationLabel}. This location is available in search, but stays are not listed here yet.`
    : hasFiltersApplied
    ? 'No properties match these filters yet. Try widening the criteria.'
    : 'No properties are available yet.';

  const handleWishlistChange = (propertyId, nextActive) => {
    if (!Number.isFinite(propertyId)) return;
    setWishlistIds((prev) => {
      const next = new Set(prev);
      if (nextActive) next.add(propertyId);
      else next.delete(propertyId);
      return next;
    });
  };

  const handleSearchSubmit = () => {
    if (searchCommitTimerRef.current) {
      window.clearTimeout(searchCommitTimerRef.current);
    }
    const searchValue = normalizeText(filters.search || filters.location);
    commitFilters((prev) => ({
      ...prev,
      search: searchValue,
      location: normalizeText(prev.location),
    }));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters, queryDestination]);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#FAF7F2] text-[#243444]">
      <header className="mx-auto w-full max-w-7xl px-4 pb-4 pt-5 sm:px-6 sm:pb-8 sm:pt-10 lg:px-8 xl:px-0">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <nav className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B97A7]">
              <Link to="/" className="hover:text-[#10233D]">Home</Link>
              <span className="mx-2 text-[#C9BFAF]">/</span>
              <Link to="/listings" className="hover:text-[#10233D]">Stays</Link>
            </nav>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#0F5C5A]">Homevilas Collection</p>
            <h1 className="font-homevilas text-[clamp(2rem,8vw,2.75rem)] font-semibold leading-none text-[#10233D]">
              Luxury Villas & Homestays
            </h1>
            <p className="max-w-2xl text-sm text-[#667085]">{headerSubtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-full border border-[#E6DED1] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6428] shadow-[0_10px_24px_rgba(16,35,61,0.06)]">
              Curated for getaways
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6 lg:px-8 xl:px-0">
        <div className="mb-4 rounded-[20px] border border-[#E6DED1] bg-white px-3 py-3 shadow-[0_14px_30px_rgba(16,35,61,0.08)] sm:rounded-[24px] sm:px-5 sm:py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="grid w-full grid-cols-2 gap-2.5 sm:gap-3 lg:flex lg:flex-wrap lg:items-center">
              <div className="relative col-span-2 flex w-full items-center lg:col-span-1 lg:w-[220px]">
              <i className="bi bi-search absolute left-3 text-[13px] text-[#C9A15B]" />
              <input
                className="hv-input h-10 w-full rounded-xl border border-[#DDD5C7] bg-white pl-8 pr-3 text-[13px] text-[#243444] outline-none placeholder:text-[#8B97A7]"
                placeholder="Search properties..."
                value={filters.search}
                onChange={(e) => {
                  setSearchFocused(true);
                  const next = e.target.value;
                  hasUserFiltersRef.current = true;
                  setFilters((s) => ({ ...s, search: next }));
                  if (searchCommitTimerRef.current) {
                    window.clearTimeout(searchCommitTimerRef.current);
                  }
                  searchCommitTimerRef.current = window.setTimeout(() => {
                    applySearchTerm(next);
                  }, 450);
                }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 120)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (searchCommitTimerRef.current) {
                      window.clearTimeout(searchCommitTimerRef.current);
                    }
                    applySearchTerm(e.currentTarget.value);
                  }
                }}
                autoComplete="off"
              />
              {shouldShowSuggestions && (
                <div className="absolute left-0 right-0 top-full z-30 mt-2 rounded-2xl border border-[#E6DED1] bg-white shadow-[0_18px_36px_rgba(16,35,61,0.12)]">
                  <div className="max-h-60 overflow-auto py-1">
                    {searchLoading ? (
                      <div className="px-3 py-2 text-xs text-[#667085]">Searching destinations...</div>
                    ) : searchSuggestions.length ? (
                      searchSuggestions.map((suggestion) => (
                        <button
                          type="button"
                          key={`${suggestion.type}-${suggestion.value}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            applySearchTerm(suggestion.value);
                            setSearchFocused(false);
                          }}
                          className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-[#F6F1E8]"
                        >
                          <div className="flex flex-1 flex-col items-start">
                            <span className="text-sm font-semibold text-[#10233D]">{suggestion.label}</span>
                            {suggestion.meta && <span className="text-xs text-[#667085]">{suggestion.meta}</span>}
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#0F5C5A]">
                            {suggestion.type}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs text-[#667085]">No matches found</div>
                    )}
                  </div>
                </div>
              )}
              </div>

              <select
                className="hv-select h-10 w-full min-w-0 rounded-xl border border-[#DDD5C7] bg-white px-3 text-[13px] font-medium text-[#243444] lg:w-auto lg:min-w-[140px]"
                value={filters.location}
                onChange={(e) =>
                  commitFilters((prev) => ({ ...prev, location: e.target.value, search: e.target.value }))
                }
              >
                <option value="">All Locations</option>
                {locationOptions.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>

              <select
                className="hv-select h-10 w-full min-w-0 rounded-xl border border-[#DDD5C7] bg-white px-3 text-[13px] font-medium text-[#243444] lg:w-auto lg:min-w-[140px]"
                value={filters.categories[0] || ''}
                onChange={(e) =>
                  commitFilters((prev) => ({ ...prev, categories: e.target.value ? [e.target.value] : [] }))
                }
              >
                <option value="">All Types</option>
                {categoryOptions.length
                  ? categoryOptions.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))
                  : ['Villa', 'Apartment', 'Cottage', 'Homestay'].map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
              </select>

              <select
                className="hv-select h-10 w-full min-w-0 rounded-xl border border-[#DDD5C7] bg-white px-3 text-[13px] font-medium text-[#243444] lg:w-auto lg:min-w-[120px]"
                value={filters.rating || ''}
                onChange={(e) =>
                  commitFilters((prev) => ({ ...prev, rating: e.target.value ? Number(e.target.value) : null }))
                }
              >
                <option value="">Any Rating</option>
                {[4.5, 4.0].map((r) => (
                  <option key={r} value={r}>
                    {`${r}+`}
                  </option>
                ))}
              </select>

              <select
                className="hv-select h-10 w-full min-w-0 rounded-xl border border-[#DDD5C7] bg-white px-3 text-[13px] font-medium text-[#243444] lg:w-auto lg:min-w-[110px]"
                value={filters.bedrooms}
                onChange={(e) => commitFilters((prev) => ({ ...prev, bedrooms: e.target.value }))}
              >
                <option value="">Any Beds</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n}+
                  </option>
                ))}
              </select>

              <div ref={priceRef} className="relative w-full min-w-0 lg:w-auto">
                <button
                  type="button"
                  className={`flex h-10 w-full items-center gap-2 rounded-xl border px-3 text-[13px] font-medium lg:w-auto ${
                    filters.priceRange[0] !== priceBounds.min || filters.priceRange[1] !== priceBounds.max
                      ? 'border-[#0F5C5A] bg-[#DDF1EE] text-[#0F5C5A]'
                      : 'border-[#DDD5C7] text-[#243444]'
                  }`}
                  onClick={() => setShowPrice((v) => !v)}
                >
                  <span>{`\u20B9 Price \u25BE`}</span>
                </button>
                {showPrice && (
                  <div className="absolute left-0 z-40 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-[#E6DED1] bg-white p-4 shadow-[0_18px_36px_rgba(16,35,61,0.12)]">
                    <PriceSlider
                      value={filters.priceRange}
                      onChange={(range) => setFilters((s) => ({ ...s, priceRange: range }))}
                      min={0}
                      max={100000}
                      formatPrice={formatPrice}
                    />
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="rounded-full bg-[#0F5C5A] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#0A4443]"
                        onClick={() => {
                          commitFilters((prev) => ({ ...prev, priceRange: prev.priceRange }));
                          setShowPrice(false);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleSearchSubmit}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#0F5C5A] px-4 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[#0A4443] lg:w-auto"
              >
                <i className="bi bi-search" aria-hidden="true" />
                Search
              </button>
            </div>

            <div className="flex w-full flex-wrap items-center gap-2.5 lg:ml-auto lg:w-auto">
              <span className="text-[12px] text-[#667085]">
                Showing {showingCount} of {totalLabelCount} stays
                {showUpdating ? <span className="ml-2 text-[11px] text-[#C9A15B]">Updating...</span> : null}
              </span>
              <div className="hidden h-6 w-px bg-[#E6DED1] sm:block" />
              <select
                className="hv-select h-10 min-w-0 flex-1 rounded-xl border border-[#DDD5C7] bg-white px-3 text-[13px] font-medium text-[#243444] sm:w-auto sm:min-w-[170px] sm:flex-none"
                value={filters.sort}
                onChange={(e) => commitFilters((prev) => ({ ...prev, sort: e.target.value }))}
              >
                <option value="recommended">Sort: Recommended</option>
                <option value="price-asc">Price Low-High</option>
                <option value="price-desc">Price High-Low</option>
                <option value="rating-desc">Top Rated</option>
                <option value="newest">Newest</option>
              </select>
              <div className="flex min-w-[168px] flex-1 items-center rounded-full border border-[#DDD5C7] bg-white p-1 sm:w-auto sm:flex-none">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={`h-7 flex-1 rounded-full px-3 text-[11px] font-semibold transition sm:flex-none ${
                    viewMode === 'grid' ? 'bg-[#10233D] text-white' : 'text-[#667085] hover:text-[#10233D]'
                  }`}
                  aria-pressed={viewMode === 'grid'}
                >
                  3 Col
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('map')}
                  className={`h-7 flex-1 rounded-full px-3 text-[11px] font-semibold transition sm:flex-none ${
                    viewMode === 'map' ? 'bg-[#0F5C5A] text-white' : 'text-[#667085] hover:text-[#10233D]'
                  }`}
                  aria-pressed={viewMode === 'map'}
                >
                  2 Col + Map
                </button>
              </div>
            </div>
          </div>
        </div>

        {activeFilterChips.length ? (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => clearFilter(chip.key)}
                className="inline-flex items-center gap-2 rounded-full border border-[#E6DED1] bg-[#F4E7CF] px-4 py-2 text-xs font-semibold text-[#8A6428]"
              >
                <span>{chip.label}</span>
                <i className="bi bi-x-lg text-[11px] leading-none" aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto text-xs font-semibold text-[#0F5C5A]"
            >
              Clear All
            </button>
          </div>
        ) : null}

        {error && (
          <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        )}

          <div className={mapMode ? 'grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]' : ''}>
          <div>
            {showSkeleton ? (
              <div className={gridClass}>
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="animate-pulse overflow-hidden rounded-[20px] border border-[#E9E2D7] bg-white"
                  >
                    <div className="h-[240px] bg-[#F3EEE6]" />
                    <div className="space-y-2 px-4 pb-4 pt-4">
                      <div className="h-4 w-3/4 rounded bg-slate-200" />
                      <div className="h-3 w-1/2 rounded bg-slate-200" />
                      <div className="h-3 w-2/3 rounded bg-slate-200" />
                      <div className="h-4 w-24 rounded bg-slate-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProperties.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-[#DDD5C7] bg-white px-4 py-6 text-center text-sm text-[#667085]">
                {emptyResultsMessage}
              </div>
            ) : (
              <div className={gridClass}>
                {paginatedProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    wishlistActive={property.propertyId ? wishlistIds.has(property.propertyId) : false}
                    onWishlistChange={handleWishlistChange}
                    formatPrice={formatPrice}
                    onHover={setHoveredId}
                  />
                ))}
              </div>
            )}

            {filteredProperties.length > pageSize && (
              <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  className="h-10 rounded-full border border-[#DDD5C7] px-4 text-sm font-semibold text-[#667085] hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const page = idx + 1;
                  const isActive = page === currentPage;
                  return (
                    <button
                      key={page}
                      type="button"
                      className={`h-10 w-10 rounded-full text-sm font-semibold ${
                        isActive
                          ? 'bg-[#10233D] text-white shadow-sm'
                          : 'border border-[#DDD5C7] text-[#667085] hover:bg-white'
                      }`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="h-10 rounded-full border border-[#DDD5C7] px-4 text-sm font-semibold text-[#667085] hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {mapMode && (
            <aside className="hidden h-[calc(100vh-140px)] w-full overflow-hidden rounded-[24px] border border-[#E6DED1] bg-white shadow-[0_16px_36px_rgba(16,35,61,0.08)] lg:sticky lg:top-[140px] lg:block">
              <MapContainer
                center={DEFAULT_MAP_CENTER}
                zoom={5}
                scrollWheelZoom={false}
                className="h-full w-full"
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapBounds points={markerItems} />
                {markerItems.map((item) => (
                  <Marker
                    key={item.id}
                    position={[item.lat, item.lng]}
                    icon={buildPriceIcon(item.priceLabel, hoveredId === item.id)}
                    eventHandlers={{
                      mouseover: () => setHoveredId(item.id),
                      mouseout: () => setHoveredId(null),
                    }}
                  >
                    <Popup>
                      <div className="w-56">
                        <img
                          src={item.images?.[0]}
                          alt={item.title}
                          className="h-24 w-full rounded-lg object-cover"
                        />
                        <div className="mt-2 text-sm font-semibold text-[#10233D]">{item.title}</div>
                        <div className="text-xs text-[#667085]">{item.location}</div>
                        <div className="mt-1 text-sm font-semibold text-[#0F5C5A]">
                          {formatPrice(item.price, item.currency)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}






